import type * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { FONT_FAMILY_MAX_LENGTH } from '@gitbook/api-client';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    FieldGroup,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient } from '../../../../state';
import type { SitesCustomizationPanelProps } from '../../types';
import type { CustomFontState } from './CustomFontState';
import { FileUploadControls } from './FileUploadControls';
import type { FontFormData } from './types';

/**
 * Dialog to create a custom font family.
 */
export function CreateCustomFontDialog(
    props: SitesCustomizationPanelProps & {
        state: CustomFontState;
    }
): React.ReactElement {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Add custom font</Heading>
                <Description>
                    The font will be available across all sites in your organization.
                </Description>
            </DialogHeader>
            <CreateCustomFontForm {...props} />
        </Dialog>
    );
}

/**
 * Form to create a custom font family.
 */
function CreateCustomFontForm(
    props: SitesCustomizationPanelProps & {
        state: CustomFontState;
    }
): React.ReactElement {
    const { organizationId, state } = props;
    const [isPending, startDialogAction] = useDialogAction();

    const form = useForm<FontFormData>({
        defaultValues: {
            fontFamily: '',
            fontSources: [],
        },
        mode: 'onChange',
    });

    const onSubmit: SubmitHandler<FontFormData> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const { data: customFont } = await apiClient.customFonts.createCustomFont(
                    organizationId,
                    {
                        fontFamily: data.fontFamily,
                        fontFaces: data.fontSources.map((fontFile) => ({
                            weight: fontFile.weight,
                            storageKey: fontFile.storageKey,
                        })),
                    }
                );

                // use the newly created font family
                state.onChange(customFont);

                toast.success('Custom font added successfully');
                await close();
            }, 'Error while adding custom font');
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <div className="flex flex-col gap-4">
                    <FieldGroup>
                        <Label>Font family</Label>
                        <Description>Give your custom font a name</Description>
                        <TextField
                            name="fontFamily"
                            required
                            minLength={1}
                            maxLength={FONT_FAMILY_MAX_LENGTH}
                        >
                            <TextInput
                                placeholder="e.g. Montserrat, Open Sans, etc."
                                autoFocus
                                disabled={isPending}
                            />
                            <FieldError />
                        </TextField>
                    </FieldGroup>
                    <FileUploadControls form={form} organizationId={organizationId} />
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Add" disabled={!form.formState.isValid} />
            </DialogFooter>
        </Form>
    );
}
